import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Building2, Radio, ShieldCheck } from "lucide-react";
import { Toaster } from "sonner";
import SiteHeader from "../components/gridvest/SiteHeader";
import Footer from "../components/gridvest/Footer";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#07111F] text-[#F8FAFC]" data-testid="about-page">
      <SiteHeader active="about" />

      <main className="relative">
        <div className="gv-grid-bg absolute inset-x-0 top-0 h-[420px]" aria-hidden />

        <section className="relative mx-auto max-w-4xl px-6 pb-16 pt-16 lg:px-8 lg:pt-24">
          <Link
            to="/"
            className="mb-10 inline-flex items-center gap-2 font-body text-sm text-[#94A3B8] transition-colors hover:text-[#F8FAFC]"
            data-testid="about-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mb-5 font-display text-[12px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
              About Us
            </p>
            <h1 className="font-display text-4xl font-extrabold leading-[1] tracking-[-0.04em] text-[#F8FAFC] sm:text-5xl lg:text-[68px]">
              Modernizing infrastructure
              <br className="hidden sm:block" /> ownership.
            </h1>
          </motion.div>
        </section>

        <section className="relative mx-auto max-w-4xl px-6 pb-20 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-8"
            >
              <p className="font-body text-lg leading-[1.8] text-[#CBD5E1]">
                InfraYield is a modern infrastructure ownership and infrastructure
                intelligence platform focused on telecom towers, billboard
                infrastructure, digital infrastructure, and infrastructure lease
                systems.
              </p>

              <p className="mt-7 font-body text-base leading-[1.8] text-[#94A3B8] sm:text-lg">
                The company aims to modernize fragmented infrastructure ownership
                markets through infrastructure technology, operational intelligence,
                and infrastructure transparency — founded by ground acquisition
                executives and asset owners.
              </p>

              <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { icon: Radio, title: "Technology", note: "Modern lease intelligence" },
                  { icon: Building2, title: "Operations", note: "Operator-led discipline" },
                  { icon: ShieldCheck, title: "Transparency", note: "Documented & reportable" },
                ].map((p) => (
                  <div
                    key={p.title}
                    className="rounded-xl border border-[#1E293B] bg-[#0F1B2D] p-5"
                  >
                    <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg border border-[#1E293B] bg-[#07111F] text-[#22C55E]">
                      <p.icon className="h-4 w-4" />
                    </div>
                    <p className="font-display text-sm font-semibold text-[#F8FAFC]">
                      {p.title}
                    </p>
                    <p className="mt-1 font-body text-xs text-[#94A3B8]">{p.note}</p>
                  </div>
                ))}
              </div>

              <div className="mt-14 flex flex-wrap items-center gap-3">
                <Link
                  to="/#early"
                  className="group inline-flex items-center gap-2 rounded-md bg-[#22C55E] px-6 py-3.5 font-body text-sm font-semibold text-[#07111F] transition-all hover:bg-[#22C55E]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
                  data-testid="about-cta-early"
                >
                  Join Early Access
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-md border border-[#334155] bg-transparent px-6 py-3.5 font-body text-sm font-semibold text-[#F8FAFC] transition-colors hover:border-[#38BDF8]/60 hover:bg-[#0F1B2D] focus:outline-none focus-visible:border-[#38BDF8] focus-visible:ring-2 focus-visible:ring-[#38BDF8]/30"
                  data-testid="about-cta-contact"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>

            <motion.aside
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-4"
            >
              <div className="overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0F1B2D]">
                <div className="border-b border-[#1E293B] px-5 py-4">
                  <p className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#94A3B8]">
                    At a glance
                  </p>
                </div>
                <ul className="divide-y divide-[#1E293B]">
                  {[
                    ["Sectors", "Telecom · Billboard · Digital"],
                    ["Focus", "Ownership & lease intelligence"],
                    ["Founders", "Industry · Owners · Investors"],
                    ["Stage", "Phase 1 · Early Access"],
                  ].map(([k, v]) => (
                    <li key={k} className="flex items-start justify-between gap-6 px-5 py-4">
                      <span className="font-body text-xs uppercase tracking-[0.12em] text-[#94A3B8]">
                        {k}
                      </span>
                      <span className="text-right font-display text-sm font-semibold text-[#F8FAFC]">
                        {v}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.aside>
          </div>
        </section>
      </main>

      <Footer />
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
